import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SignpostIcon from '@mui/icons-material/Signpost';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrafficIcon from '@mui/icons-material/Traffic';
import CropFreeIcon from '@mui/icons-material/CropFree';
import HexagonIcon from '@mui/icons-material/Hexagon';
import LinkIcon from '@mui/icons-material/Link';
import EditRoadIcon from '@mui/icons-material/EditRoad';
import type { PropertiesPanelProps, RoadOverlaySettings } from '../types/selection';
import type { DelineatorDeviceType } from '../types/delineators';

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedElement,
  onSignRotate,
  onSignResize,
  onSignMove,
  onSignCalloutMove,
  onSignDelete,
  onArrowDistanceChange,
  onArrowLabelVisibilityChange,
  onArrowDelete,
  onDelineatorUpdate,
  onDelineatorDelete,
  onWorkzoneDelete,
  onPaperSizeChange,
  onMapFadeChange,
  onRoadOverlaySettingsChange,
}) => {
  // No selection - show empty state
  if (!selectedElement) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No element selected
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click an element on the map to edit
        </Typography>
      </Box>
    );
  }

  // Render based on element type
  switch (selectedElement.type) {
    case 'sign':
      return (
        <SignProperties
          sign={selectedElement.data}
          onRotate={onSignRotate}
          onResize={onSignResize}
          onMove={onSignMove}
          onCalloutMove={onSignCalloutMove}
          onDelete={onSignDelete}
        />
      );
    case 'arrow':
      return (
        <ArrowProperties
          arrow={selectedElement.data}
          onDistanceChange={onArrowDistanceChange}
          onLabelVisibilityChange={onArrowLabelVisibilityChange}
          onDelete={onArrowDelete}
        />
      );
    case 'delineator':
      return (
        <DelineatorProperties
          delineator={selectedElement.data}
          onUpdate={onDelineatorUpdate}
          onDelete={onDelineatorDelete}
        />
      );
    case 'workzone':
      return (
        <WorkzoneProperties
          workzone={selectedElement.data}
          onDelete={onWorkzoneDelete}
        />
      );
    case 'boundingbox':
      return (
        <BoundingBoxProperties
          onPaperSizeChange={onPaperSizeChange}
          onMapFadeChange={onMapFadeChange}
        />
      );
    case 'roadoverlay':
      return (
        <RoadOverlayProperties
          settings={selectedElement.data}
          onSettingsChange={onRoadOverlaySettingsChange}
        />
      );
    default:
      return null;
  }
};

// Sign Properties Sub-component
interface SignPropertiesProps {
  sign: any;
  onRotate?: (id: string, rotation: number) => void;
  onResize?: (id: string, scale: number) => void;
  onMove?: (id: string, position: { lat: number; lng: number }) => void;
  onCalloutMove?: (id: string, offset: { x: number; y: number }) => void;
  onDelete?: (id: string) => void;
}

const SignProperties: React.FC<SignPropertiesProps> = ({
  sign,
  onRotate,
  onResize,
  onMove,
  onCalloutMove,
  onDelete,
}) => {
  const [rotation, setRotation] = useState(sign.rotation || 0);
  const [scale, setScale] = useState(sign.scale || 1);

  // Sync state when sign changes
  useEffect(() => {
    setRotation(sign.rotation || 0);
    setScale(sign.scale || 1);
  }, [sign.rotation, sign.scale]);

  const handleRotationChange = useCallback((value: number) => {
    setRotation(value);
    onRotate?.(sign.id, value);
  }, [sign.id, onRotate]);

  const handleScaleChange = useCallback((value: number) => {
    setScale(value);
    onResize?.(sign.id, value);
  }, [sign.id, onResize]);

  const signCode = sign.sign?.code_sa || 'Unknown';

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <SignpostIcon sx={{ color: '#6366F1', fontSize: 20 }} />
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>Sign</Typography>
          <Typography variant="caption" color="text.secondary">{signCode}</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Rotation Control */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" fontWeight={500} color="text.primary">Rotation</Typography>
          <Typography variant="caption" sx={{ color: '#333', fontWeight: 600 }}>{rotation}°</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
          {[0, 90, 180, 270].map((deg) => (
            <Button
              key={deg}
              size="small"
              variant={rotation === deg ? 'contained' : 'outlined'}
              onClick={() => handleRotationChange(deg)}
              sx={{
                minWidth: 0,
                flex: 1,
                py: 0.5,
                px: 0.5,
                fontSize: '0.7rem',
                borderColor: rotation === deg ? '#A51211' : '#d0d0d0',
                color: rotation === deg ? 'white' : '#666',
                bgcolor: rotation === deg ? '#A51211' : 'transparent',
                '&:hover': {
                  borderColor: '#A51211',
                  bgcolor: rotation === deg ? '#8a0f0e' : 'rgba(165, 18, 17, 0.06)',
                },
              }}
            >
              {deg}°
            </Button>
          ))}
        </Box>
        <Slider 
          value={rotation} 
          onChange={(_, value) => handleRotationChange(value as number)} 
          min={0} 
          max={360} 
          step={1}
          size="small"
          sx={{
            color: '#333',
            '& .MuiSlider-thumb': {
              width: 14,
              height: 14,
              bgcolor: '#333',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 6px rgba(0, 0, 0, 0.1)',
              },
            },
            '& .MuiSlider-track': {
              height: 4,
              bgcolor: '#333',
            },
            '& .MuiSlider-rail': {
              height: 4,
              bgcolor: '#e0e0e0',
            },
          }}
        />
      </Box>

      {/* Scale Control */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" fontWeight={500} color="text.primary">Scale</Typography>
          <Typography variant="caption" sx={{ color: '#333', fontWeight: 600 }}>{scale.toFixed(1)}x</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
          {[0.5, 1, 1.5, 2].map((s) => (
            <Button
              key={s}
              size="small"
              variant={Math.abs(scale - s) < 0.05 ? 'contained' : 'outlined'}
              onClick={() => handleScaleChange(s)}
              sx={{
                minWidth: 0,
                flex: 1,
                py: 0.5,
                px: 0.5,
                fontSize: '0.7rem',
                borderColor: Math.abs(scale - s) < 0.05 ? '#A51211' : '#d0d0d0',
                color: Math.abs(scale - s) < 0.05 ? 'white' : '#666',
                bgcolor: Math.abs(scale - s) < 0.05 ? '#A51211' : 'transparent',
                '&:hover': {
                  borderColor: '#A51211',
                  bgcolor: Math.abs(scale - s) < 0.05 ? '#8a0f0e' : 'rgba(165, 18, 17, 0.06)',
                },
              }}
            >
              {s}x
            </Button>
          ))}
        </Box>
        <Slider 
          value={scale} 
          onChange={(_, value) => handleScaleChange(value as number)} 
          min={0.5} 
          max={3} 
          step={0.1}
          size="small"
          sx={{
            color: '#333',
            '& .MuiSlider-thumb': {
              width: 14,
              height: 14,
              bgcolor: '#333',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 6px rgba(0, 0, 0, 0.1)',
              },
            },
            '& .MuiSlider-track': {
              height: 4,
              bgcolor: '#333',
            },
            '& .MuiSlider-rail': {
              height: 4,
              bgcolor: '#e0e0e0',
            },
          }}
        />
      </Box>

      {sign.edgeMetadata && sign.edgeMetadata.roadSegmentId && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <LinkIcon sx={{ fontSize: 14, color: '#6B7280' }} />
            <Typography variant="caption" fontWeight={500}>Snapped to Road</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip label={`${sign.edgeMetadata.side}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize', height: 22 }} />
            <Chip label={`${sign.edgeMetadata.edgeAngle}°`} size="small" variant="outlined" sx={{ height: 22 }} />
          </Box>
          {sign.signLineId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Part of sign line
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1.5 }} />
      <Button variant="outlined" color="error" startIcon={<DeleteIcon />} fullWidth onClick={() => onDelete?.(sign.id)} size="small">Delete</Button>
    </Box>
  );
};

// Arrow Properties
interface ArrowPropertiesProps {
  arrow: any;
  onDistanceChange?: (id: string, distance: number) => void;
  onLabelVisibilityChange?: (id: string, show: boolean) => void;
  onDelete?: (id: string) => void;
}

const ArrowProperties: React.FC<ArrowPropertiesProps> = ({ arrow, onDistanceChange, onLabelVisibilityChange, onDelete }) => {
  const [distance, setDistance] = useState(arrow.distance || 0);
  const isAutoGenerated = arrow.isAutoGenerated || false;
  const [showDistanceLabel, setShowDistanceLabel] = useState(arrow.showDistanceLabel !== false);

  useEffect(() => {
    setDistance(arrow.distance || 0);
    setShowDistanceLabel(arrow.showDistanceLabel !== false);
  }, [arrow.distance, arrow.showDistanceLabel]);

  const handleDistanceChange = useCallback((value: number) => {
    setDistance(value);
    onDistanceChange?.(arrow.id, value);
  }, [arrow.id, onDistanceChange]);

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TimelineIcon sx={{ color: isAutoGenerated ? '#111827' : '#DB2777', fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>{isAutoGenerated ? 'Auto Arrow' : 'Arrow'}</Typography>
            {isAutoGenerated && <Chip label="Auto" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#F3F4F6', color: '#374151' }} />}
          </Box>
        </Box>
      </Box>

      {isAutoGenerated && (arrow.startSignId || arrow.endSignId) && (
        <Box sx={{ mb: 1.5, p: 1, bgcolor: '#F9FAFB', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LinkIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
            <Typography variant="caption" color="text.secondary">Auto-updates when signs move</Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>Distance (m)</Typography>
        <TextField type="number" size="small" fullWidth value={distance} onChange={(e) => handleDistanceChange(parseFloat(e.target.value) || 0)} inputProps={{ min: 0, step: 0.1 }} />
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showDistanceLabel}
              onChange={(e) => {
                const nextValue = e.target.checked;
                setShowDistanceLabel(nextValue);
                onLabelVisibilityChange?.(arrow.id, nextValue);
              }}
            />
          }
          label={<Typography variant="body2">Show label</Typography>}
          sx={{ m: 0 }}
        />
      </Box>

      <Divider sx={{ mb: 1.5 }} />
      <Button variant="outlined" color="error" startIcon={<DeleteIcon />} fullWidth onClick={() => onDelete?.(arrow.id)} size="small">Delete</Button>
    </Box>
  );
};

// Delineator Properties
interface DelineatorPropertiesProps {
  delineator: any;
  onUpdate?: (id: string, updates: any) => void;
  onDelete?: (id: string) => void;
}

const DelineatorProperties: React.FC<DelineatorPropertiesProps> = ({ delineator, onUpdate, onDelete }) => {
  const [deviceType, setDeviceType] = useState<DelineatorDeviceType>(delineator.deviceType);
  const [spacing, setSpacing] = useState(delineator.spacing);
  const [deviceSize, setDeviceSize] = useState(delineator.deviceSize);

  const handleDeviceTypeChange = useCallback((value: DelineatorDeviceType) => {
    setDeviceType(value);
    onUpdate?.(delineator.id, { deviceType: value });
  }, [delineator.id, onUpdate]);

  const handleSpacingChange = useCallback((value: number) => {
    setSpacing(value);
    onUpdate?.(delineator.id, { spacing: value });
  }, [delineator.id, onUpdate]);

  const handleDeviceSizeChange = useCallback((value: number) => {
    setDeviceSize(value);
    onUpdate?.(delineator.id, { deviceSize: value });
  }, [delineator.id, onUpdate]);

  const deviceCount = delineator.path ? Math.ceil(
    delineator.path.reduce((total: number, point: any, idx: number, arr: any[]) => {
      if (idx === 0) return 0;
      const prev = arr[idx - 1];
      const dist = Math.sqrt(Math.pow((point.lat - prev.lat) * 111000, 2) + Math.pow((point.lng - prev.lng) * 111000 * Math.cos(point.lat * Math.PI / 180), 2));
      return total + dist;
    }, 0) / spacing
  ) + 1 : 0;

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TrafficIcon sx={{ color: '#F97316', fontSize: 20 }} />
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>Delineators</Typography>
          <Typography variant="caption" color="text.secondary">{deviceCount} devices</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ mb: 1.5 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Type</InputLabel>
          <Select value={deviceType} label="Type" onChange={(e) => handleDeviceTypeChange(e.target.value as DelineatorDeviceType)}>
            <MenuItem value="cone">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2L6 20H18L12 2Z" fill="#F97316" stroke="#EA580C" strokeWidth="1"/></svg>
                Cone
              </Box>
            </MenuItem>
            <MenuItem value="yellow_dot">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" fill="#E6A224"/><circle cx="12" cy="12" r="6.5" fill="#FF4500"/></svg>
                Traffic Dot
              </Box>
            </MenuItem>
            <MenuItem value="barrel">Barrel</MenuItem>
            <MenuItem value="bollard">Bollard</MenuItem>
            <MenuItem value="delineator">Delineator Post</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>Spacing (m)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Slider value={spacing} onChange={(_, value) => handleSpacingChange(value as number)} min={0.5} max={10} step={0.5} sx={{ flex: 1 }} size="small" />
          <TextField type="number" size="small" value={spacing} onChange={(e) => handleSpacingChange(parseFloat(e.target.value) || 1)} inputProps={{ min: 0.5, max: 10, step: 0.5 }} sx={{ width: 60 }} />
        </Box>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>Size</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Slider value={deviceSize} onChange={(_, value) => handleDeviceSizeChange(value as number)} min={0.5} max={2} step={0.1} sx={{ flex: 1 }} size="small" />
          <Typography variant="body2" sx={{ minWidth: 35, textAlign: 'right' }}>{deviceSize.toFixed(1)}x</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />
      <Button variant="outlined" color="error" startIcon={<DeleteIcon />} fullWidth onClick={() => onDelete?.(delineator.id)} size="small">Delete</Button>
    </Box>
  );
};

// Workzone Properties
interface WorkzonePropertiesProps {
  workzone: any;
  onDelete?: (id: string) => void;
}

const WorkzoneProperties: React.FC<WorkzonePropertiesProps> = ({ workzone, onDelete }) => {
  const pointCount = workzone.polygon?.length || 0;

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <HexagonIcon sx={{ color: '#10B981', fontSize: 20 }} />
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>Workzone</Typography>
          <Typography variant="caption" color="text.secondary">{pointCount} vertices</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />
      <Button variant="outlined" color="error" startIcon={<DeleteIcon />} fullWidth onClick={() => onDelete?.(workzone.id)} size="small">Delete</Button>
    </Box>
  );
};

// Bounding Box Properties
interface BoundingBoxPropertiesProps {
  onPaperSizeChange?: (size: string, orientation: string) => void;
  onMapFadeChange?: (opacity: number) => void;
}

const BoundingBoxProperties: React.FC<BoundingBoxPropertiesProps> = ({ onPaperSizeChange, onMapFadeChange }) => {
  const [paperSize, setPaperSize] = useState('A3');
  const [orientation, setOrientation] = useState('landscape');
  const [mapFade, setMapFade] = useState(0);

  const handlePaperChange = useCallback((size: string, orient: string) => {
    setPaperSize(size);
    setOrientation(orient);
    onPaperSizeChange?.(size, orient);
  }, [onPaperSizeChange]);

  const handleFadeChange = useCallback((value: number) => {
    setMapFade(value);
    onMapFadeChange?.(value);
  }, [onMapFadeChange]);

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CropFreeIcon sx={{ color: '#3B82F6', fontSize: 20 }} />
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>Print Area</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ mb: 1.5 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Paper</InputLabel>
          <Select value={paperSize} label="Paper" onChange={(e) => handlePaperChange(e.target.value, orientation)}>
            <MenuItem value="A4">A4</MenuItem>
            <MenuItem value="A3">A3</MenuItem>
            <MenuItem value="A2">A2</MenuItem>
            <MenuItem value="A1">A1</MenuItem>
            <MenuItem value="A0">A0</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Orientation</InputLabel>
          <Select value={orientation} label="Orientation" onChange={(e) => handlePaperChange(paperSize, e.target.value)}>
            <MenuItem value="landscape">Landscape</MenuItem>
            <MenuItem value="portrait">Portrait</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>Map Fade</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Slider value={mapFade} onChange={(_, value) => handleFadeChange(value as number)} min={0} max={1} step={0.1} sx={{ flex: 1 }} size="small" />
          <Typography variant="body2" sx={{ minWidth: 35, textAlign: 'right' }}>{Math.round(mapFade * 100)}%</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Road Overlay Properties
interface RoadOverlayPropertiesProps {
  settings: RoadOverlaySettings;
  onSettingsChange?: (settings: Partial<RoadOverlaySettings>) => void;
}

const RoadOverlayProperties: React.FC<RoadOverlayPropertiesProps> = ({ settings, onSettingsChange }) => {
  const [showOnlyNearSigns, setShowOnlyNearSigns] = useState(settings.showOnlyNearSigns);

  // Sync with external settings changes
  useEffect(() => {
    setShowOnlyNearSigns(settings.showOnlyNearSigns);
  }, [settings]);

  const handleShowOnlyNearSignsChange = useCallback((value: boolean) => {
    setShowOnlyNearSigns(value);
    onSettingsChange?.({ showOnlyNearSigns: value });
  }, [onSettingsChange]);

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <EditRoadIcon sx={{ color: '#1976d2', fontSize: 20 }} />
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>Road Overlay</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showOnlyNearSigns}
              onChange={(e) => handleShowOnlyNearSignsChange(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Filter to signed roads</Typography>}
          sx={{ m: 0 }}
        />
      </Box>

      {showOnlyNearSigns && (
        <Box sx={{ p: 1, bgcolor: '#EFF6FF', borderRadius: 1 }}>
          <Typography variant="caption" color="#1E40AF">
            Only shows road markings near placed signs
          </Typography>
        </Box>
      )}
    </Box>
  );
};
